/**
 * MODULE: platform/browser/frontend/components/BrowserEmptyState.tsx
 * PURPOSE: Idle viewport before a browser session starts — assistant page colors/layout.
 * RECENT: 2026-06-18 — ASSISTANT_RAIL_BG_CLASS + BrowserAssistantIdleLanding parity.
 */

import { BROWSER_MISSIONS, type BrowserMission } from '../data/browserMissions';
import { BrowserAssistantIdleLanding } from './BrowserAssistantIdleLanding';
import { BrowserMissionPicker } from './BrowserMissionPicker';

interface BrowserEmptyStateProps {
  isConnecting: boolean;
  onStart: () => void;
  startLabel?: string;
  /** When true, show a primary button (e.g. reconnect after expiry). Default: hint only. */
  showPrimaryButton?: boolean;
  onStartMission?: (mission: BrowserMission) => void;
  providerLabel?: string;
  providerReady?: boolean;
}

export function BrowserEmptyState({
  isConnecting,
  onStart,
  startLabel,
  showPrimaryButton = false,
  onStartMission,
  providerLabel,
  providerReady = false,
}: BrowserEmptyStateProps) {
  const showButton = showPrimaryButton || Boolean(startLabel);
  const showMobileMissions = Boolean(onStartMission) && !showButton;

  return (
    <BrowserAssistantIdleLanding
      providerLabel={providerLabel}
      providerReady={providerReady}
      testId="browser-empty-state"
      footer={
        showButton ? (
          <div className="px-4 pb-4 text-center">
            <button
              type="button"
              onClick={onStart}
              disabled={isConnecting}
              className="inline-flex items-center justify-center rounded-full bg-gray-900 px-5 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              data-testid="browser-empty-start-button"
            >
              {isConnecting ? 'Connecting…' : startLabel ?? 'Start Browser'}
            </button>
          </div>
        ) : null
      }
    >
      {showMobileMissions && onStartMission ? (
        <div className="md:hidden" data-testid="browser-mobile-mission-picker">
          <BrowserMissionPicker
            missions={BROWSER_MISSIONS}
            disabled={isConnecting}
            onSelect={onStartMission}
          />
        </div>
      ) : null}
      {!showMobileMissions && !showButton ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <span className="md:hidden">Use the command panel below or Start Browser in the toolbar.</span>
          <span className="hidden md:inline">Use the command panel on the right or Start Browser in the toolbar.</span>
        </p>
      ) : null}
    </BrowserAssistantIdleLanding>
  );
}
